import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIsAdmin } from '@hooks/usePermission';
import {
  FiCalendar,
  FiInfo,
  FiClock,
  FiMonitor,
  FiEyeOff,
  FiBell,
  FiClipboard,
  FiShield,
  FiSliders,
  FiCheckCircle,
} from 'react-icons/fi';

import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';
import { Button, buttonVariants } from '@components/Button';
import { Dropdown, borderedSelectStyles } from '@components/Dropdown';
import { DaySelector } from '@components/DaySelector';
import { Callout, calloutVariants } from '@components/Callout';
import { Toggle } from '@components/Toggle';
import { ConfirmActionDialog } from '@components/ConfirmActionDialog';
import { addNotification, notificationStates } from '@components/Notification';
import { useWorkingSchedule } from '@/hooks/useWorkingSchedule';
import { useStealthMode } from '@/hooks/useStealthMode';
import { useScreenshotsEnabled } from '@/hooks/useScreenshotsEnabled';
import companyService from '@/services/company';

import { SettingsPanelHeader } from './SettingsPanelHeader.component';
import { SettingsFieldCard } from './SettingsFieldCard.component';
import { QuickSettingCard } from './QuickSettingCard.component';
import {
  DAYS_OF_WEEK,
  DEFAULT_WORKING_DAYS,
  TIME_OPTIONS,
  QUICK_SETTINGS_CARDS,
  settingsNavKeys,
} from '../SettingsRedesign.constants';

import styles from './CompanySection.module.scss';

const QUICK_CARD_ICON_MAP = {
  screenshotsMonitoring: FiMonitor,
  stealthMode: FiEyeOff,
  alertsNotifications: FiBell,
  leaveAttendance: FiClipboard,
  privacySecurity: FiShield,
  advanced: FiSliders,
};

// Quick-settings cards that are a straight enable/disable toggle (admin-gated,
// confirm-before-apply) rather than a link into another nav section. Each
// entry describes copy + the save call for the shared ConfirmActionDialog
// (same reusable confirm popup used on EmployeeList).
const TOGGLE_CARD_CONFIG = {
  stealthMode: {
    icon: <FiEyeOff size={18} color="var(--rose-600)" />,
    iconBg: 'rose-100',
    title: 'Confirm Stealth Mode Change',
    message: (value) =>
      `Are you sure you want to ${value ? 'enable' : 'disable'} stealth mode?`,
    confirmLabel: (value) =>
      value ? 'Enable Stealth Mode' : 'Disable Stealth Mode',
    successMessage: (value) =>
      `Stealth mode ${value ? 'enabled' : 'disabled'} successfully`,
    errorMessage: 'Failed to update stealth mode',
    save: (companyId, value) =>
      companyService.updateStealthMode({ companyId, enabled: value }),
  },
  [settingsNavKeys.screenshotsMonitoring]: {
    icon: <FiMonitor size={18} color="var(--blue-600)" />,
    iconBg: 'blue-100',
    title: 'Confirm Screenshot Capture Change',
    message: (value) =>
      `Are you sure you want to ${value ? 'enable' : 'disable'} screenshot capture?`,
    confirmLabel: (value) =>
      value ? 'Enable Screenshot Capture' : 'Disable Screenshot Capture',
    successMessage: (value) =>
      `Screenshot capture ${value ? 'enabled' : 'disabled'} successfully`,
    errorMessage: 'Failed to update screenshot capture setting',
    save: (companyId, value) =>
      companyService.updateScreenshotsEnabled({ companyId, enabled: value }),
  },
};

const findOption = (options, value) =>
  options.find((option) => option.value === value) || null;

export const CompanySection = ({ onNavigate }) => {
  const userProfile = useSelector((state) => state.auth.profile);
  const isAdmin = useIsAdmin();
  const { workingSchedule } = useWorkingSchedule();
  const { stealthMode } = useStealthMode();
  const { screenshotsEnabled } = useScreenshotsEnabled();

  const toggleCardValues = {
    stealthMode,
    [settingsNavKeys.screenshotsMonitoring]: screenshotsEnabled,
  };

  const [selectedDays, setSelectedDays] = useState(DEFAULT_WORKING_DAYS);
  const [startTime, setStartTime] = useState('09:30 AM');
  const [endTime, setEndTime] = useState('06:30 PM');
  const [saving, setSaving] = useState(false);
  // { cardKey, pendingValue } for whichever toggle card was last confirmed —
  // only ever overwritten on the next open, never nulled on close, so the
  // dialog's content stays put while its exit animation plays instead of
  // collapsing to empty mid-fade.
  const [activeToggleConfirm, setActiveToggleConfirm] = useState(null);
  const [toggleConfirmOpen, setToggleConfirmOpen] = useState(false);
  const [togglePending, setTogglePending] = useState(false);

  // Load the company's saved schedule into the editable fields whenever it
  // changes (initial fetch, or a live update from another admin's save).
  useEffect(() => {
    setSelectedDays(workingSchedule.days);
    setStartTime(workingSchedule.startTime);
    setEndTime(workingSchedule.endTime);
  }, [workingSchedule]);

  const toggleDay = (dayKey) => {
    setSelectedDays((prev) =>
      prev.includes(dayKey)
        ? prev.filter((key) => key !== dayKey)
        : [...prev, dayKey]
    );
  };

  const handleSave = async () => {
    if (!isAdmin) {
      addNotification({
        message: 'Access denied: Only administrators can update working days',
        level: notificationStates.error,
      });
      return;
    }

    setSaving(true);
    try {
      const result = await companyService.updateWorkingSchedule({
        companyId: userProfile.activeCompany.companyId,
        days: selectedDays,
        startTime,
        endTime,
        updatedBy: userProfile.id,
      });
      if (!result.success) throw new Error(result.error);

      addNotification({
        message: 'Company settings saved',
        level: notificationStates.success,
      });
    } catch (error) {
      console.error('Error saving working schedule:', error);
      addNotification({
        message: 'Failed to save company settings',
        level: notificationStates.error,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleCardChange = (cardKey, newValue) => {
    if (!isAdmin) {
      addNotification({
        message: 'Access denied: Only administrators can change this setting',
        level: notificationStates.error,
      });
      return;
    }
    setActiveToggleConfirm({ cardKey, pendingValue: newValue });
    setToggleConfirmOpen(true);
  };

  const activeToggleCardConfig =
    activeToggleConfirm && TOGGLE_CARD_CONFIG[activeToggleConfirm.cardKey];

  const handleConfirmToggle = async () => {
    setTogglePending(true);
    try {
      const result = await activeToggleCardConfig.save(
        userProfile?.activeCompany?.companyId,
        activeToggleConfirm.pendingValue
      );
      if (!result.success) throw new Error(result.error);

      addNotification({
        message: activeToggleCardConfig.successMessage(
          activeToggleConfirm.pendingValue
        ),
        level: notificationStates.success,
      });
      setToggleConfirmOpen(false);
    } catch (error) {
      console.error(`Error updating "${activeToggleCardConfig.title}":`, error);
      addNotification({
        message: activeToggleCardConfig.errorMessage,
        level: notificationStates.error,
      });
    } finally {
      setTogglePending(false);
    }
  };

  return (
    <>
      <SettingsPanelHeader
        icon={<FiCalendar size={20} color="var(--indigo-600)" />}
        title="Company Working Days"
        description="Select the standard working days for your company. These days will be considered as working days for attendance, timesheets and reports."
      />

      <DaySelector
        days={DAYS_OF_WEEK}
        selectedKeys={selectedDays}
        onChange={toggleDay}
      />

      <Callout variant={calloutVariants.info} icon={<FiInfo size={14} />}>
        Weekend days (unselected) will be treated as non-working days. Employees
        will not be expected to log time on these days.
      </Callout>

      <div className={styles.hoursHeading}>
        <Text
          variant={textVariantsEnum.Text_md}
          fontWeight={fontWeightEnum.Semibold}
          color="gray-900"
        >
          Working Hours <span className={styles.optional}>(Optional)</span>
        </Text>
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Normal}
          color="gray-600"
        >
          Define the default working hours and break duration for your company.
        </Text>
      </div>

      <div className={styles.hoursRow}>
        <SettingsFieldCard
          icon={<FiClock size={16} color="var(--indigo-600)" />}
          label="Start Time"
        >
          <Dropdown
            options={TIME_OPTIONS}
            value={findOption(TIME_OPTIONS, startTime)}
            onChange={(option) => setStartTime(option.value)}
            selectStyles={borderedSelectStyles}
          />
        </SettingsFieldCard>
        <span className={styles.dash}>–</span>
        <SettingsFieldCard
          icon={<FiClock size={16} color="var(--indigo-600)" />}
          label="End Time"
        >
          <Dropdown
            options={TIME_OPTIONS}
            value={findOption(TIME_OPTIONS, endTime)}
            onChange={(option) => setEndTime(option.value)}
            selectStyles={borderedSelectStyles}
          />
        </SettingsFieldCard>
      </div>

      <div className={styles.saveRow}>
        <Button
          text="Save Changes"
          variant={buttonVariants.primary}
          icon={<FiCheckCircle size={16} />}
          onClick={handleSave}
          loading={saving}
        />
      </div>

      <div className={styles.quickGrid}>
        {QUICK_SETTINGS_CARDS.map((card) => {
          const Icon = QUICK_CARD_ICON_MAP[card.key];
          const toggleConfig = TOGGLE_CARD_CONFIG[card.key];
          return (
            <QuickSettingCard
              key={card.key}
              icon={<Icon size={18} color={`var(--${card.iconColor})`} />}
              iconBg={card.iconBg}
              title={card.title}
              description={card.description}
              status={card.status}
              onClick={
                card.navKey ? () => onNavigate?.(card.navKey) : undefined
              }
              control={
                toggleConfig ? (
                  <Toggle
                    checked={!!toggleCardValues[card.key]}
                    onChange={(newValue) =>
                      handleToggleCardChange(card.key, newValue)
                    }
                  />
                ) : undefined
              }
            />
          );
        })}
      </div>

      {activeToggleCardConfig && (
        <ConfirmActionDialog
          visible={toggleConfirmOpen}
          onHide={() => setToggleConfirmOpen(false)}
          icon={activeToggleCardConfig.icon}
          iconBg={activeToggleCardConfig.iconBg}
          title={activeToggleCardConfig.title}
          message={activeToggleCardConfig.message(
            activeToggleConfirm.pendingValue
          )}
          confirmLabel={activeToggleCardConfig.confirmLabel(
            activeToggleConfirm.pendingValue
          )}
          confirmVariant={buttonVariants.primary}
          onConfirm={handleConfirmToggle}
          loading={togglePending}
        />
      )}
    </>
  );
};

CompanySection.displayName = 'CompanySection';
